import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AbstractFacade } from './abstract.facade';

export interface ItemsPaginableState<Item> {
  loading: boolean;
  total: number | null;
  items: Array<Item>;
  lastRequestedIndex: number;
  lastLoadingTime: number | null;
}

export abstract class AbstractPaginableFacade<
  Item,
  T extends ItemsPaginableState<Item>,
> extends AbstractFacade {
  protected cacheValidityTime = 0;
  protected state: T;
  public readonly store: BehaviorSubject<T>;
  public readonly state$: Observable<T>;
  protected subscriptions: Subscription;

  constructor(
    protected readonly initialState: T,
    public readonly cacheValidity = 180000
  ) {
    super();
    this.state = this.initialState;
    this.store = new BehaviorSubject<T>(this.state);
    this.state$ = this.store.asObservable();
  }

  getState$(): Observable<T> {
    return this.state$;
  }

  protected setState(newState: Partial<T>): void {
    this.state = { ...this.state, ...newState };
    this.store.next(this.state);
  }

  reset(): void {
    this.setState(this.initialState);
    this.subscriptions?.unsubscribe();
  }

  loadFromStart(count: number): void {
    const end: number = count - 1;
    if (this.isCacheValid()) {
      // Check if there is still something to load
      if (end > this.state.lastRequestedIndex + 1) {
        this.loadFromApi(this.state.lastRequestedIndex + 1, end);
      }
      return;
    }

    this.reset();
    this.loadFromApi(0, end);
  }

  load(start: number, count: number): void {
    if (start <= 0) {
      return this.loadFromStart(count);
    }
    const end: number = start + count - 1;
    // Check if there is still something to load
    if (end > this.state.lastRequestedIndex + 1) {
      this.loadFromApi(this.state.lastRequestedIndex + 1, end);
    }
  }

  abstract loadFromApi(start: number, end: number);

  protected computePagesAndSizeFromInterval(
    start: number,
    end: number
  ): { page: number; pageSize: number } {
    const numberWantedItems: number = end - start + 1;
    if (start <= 0 || start < numberWantedItems) {
      return {
        page: 1,
        pageSize: end + 1,
      };
    }

    for (let size = numberWantedItems; size <= start; ++size) {
      const pageBefore: number = Math.floor(start / size);
      const newStart: number = pageBefore * size;
      if (newStart + size > end) {
        return {
          page: pageBefore + 1,
          pageSize: size,
        };
      }
    }

    // Should never go here
    return {
      page: 1,
      pageSize: end + 1,
    };
  }

  /**
   * It places new items at the right index in the array
   */
  protected mergeNewItemsToOldOnes(newItems: Array<Item>, offset: number) {
    const mergedItems = [...this.state.items];
    newItems.forEach((item, index) => (mergedItems[index + offset] = item));
    return mergedItems;
  }

  isFirstPageLoaded(): boolean {
    return this.state.total !== null;
  }

  isTotallyLoaded(): boolean {
    return (
      this.state.total !== null && this.state.items.length >= this.state.total
    );
  }

  isLoading(): boolean {
    return this.state.loading;
  }

  isCacheValid() {
    return this.cacheValidityTime > Date.now();
  }
}
